import { useEffect, useRef, useState } from "react";

/**
 * /login is a transient redirector to the Emergent-managed Google OAuth
 * gateway. We DON'T render a branded "Sign in" card on the happy path —
 * the user came here because they're not authenticated, so kick them
 * straight into the OAuth flow. Eliminates the double-CTA UX where the
 * user would otherwise see a "Sign in with Google" button on this page
 * AND another one on the Emergent gateway page.
 *
 * The one exception is when the OAuth flow bounces back with
 * ?error=restricted (meaning Google auth succeeded but their domain isn't
 * on the allow-list) — we DO render a card so they know why they were
 * denied and can try a different account.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. Always derive `redirectUrl` from
 * window.location.origin so the cookie domain matches.
 */
export default function Login() {
  const [restricted, setRestricted] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const hasRedirected = useRef(false);

  const initiateSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH. Always derive from window.location.origin.
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (hasRedirected.current) return;
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err === "restricted") {
      setRestricted(true);
      return;
    }
    if (err === "auth_failed") {
      // AuthContext detected a redirect loop — session cookie is being
      // rejected (third-party cookie block, missing same-site config, etc).
      // Show a useful error card instead of bouncing the user again.
      setAuthFailed(true);
      return;
    }
    // Happy path: redirect straight into Emergent OAuth.
    hasRedirected.current = true;
    initiateSignIn();
  }, []);

  // Render a calm in-brand interstitial while the redirect kicks off.
  // The browser usually navigates within ~80ms so this is barely seen,
  // but we want SOMETHING on screen rather than a flash of nothing.
  if (!restricted && !authFailed) {
    return (
      <div
        data-testid="login-page"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--color-bg-siphon, #100030)",
          fontFamily: "var(--font-family, 'Outfit', sans-serif)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            aria-hidden="true"
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, #FA98FF, #F050F8 55%, #600E6B)",
              boxShadow:
                "0 0 16px rgba(240,80,248,0.55), inset 0 0 8px rgba(255,255,255,0.15)",
              margin: "0 auto 1rem",
            }}
          />
          <p
            data-testid="login-redirect-text"
            style={{
              color: "var(--color-bg-purple-haze, #A49FC8)",
              fontSize: "0.92rem",
              fontWeight: 300,
              margin: 0,
            }}
          >
            Redirecting you to sign in…
          </p>
        </div>
      </div>
    );
  }

  // Auth-loop state — surfaced when AuthContext detects more than 3 failed
  // /auth/me calls in 60s. Almost always means the session cookie isn't
  // making it back to the server (third-party cookie blocked by Safari ITP /
  // Firefox ETP / strict Chrome settings).
  if (authFailed) {
    return (
      <div
        data-testid="login-page"
        style={{
          minHeight: "100vh",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          overflow: "hidden",
          background: "var(--color-bg-siphon)",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "-30%",
            right: "-20%",
            width: "900px",
            height: "700px",
            background:
              "radial-gradient(ellipse, rgba(255,62,104,0.20) 0%, rgba(88,64,255,0.10) 45%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "2rem",
            left: "2rem",
            display: "flex",
            alignItems: "center",
            gap: "0.55rem",
            zIndex: 2,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, #FA98FF, #F050F8 55%, #600E6B)",
              boxShadow:
                "0 0 14px rgba(240,80,248,0.55), inset 0 0 8px rgba(255,255,255,0.15)",
            }}
          />
          <span
            style={{
              color: "var(--color-bg-ice)",
              fontWeight: 500,
              fontSize: "1.05rem",
              letterSpacing: "-0.01em",
            }}
          >
            ProcOps
          </span>
        </div>
        <div
          style={{
            maxWidth: "480px",
            width: "100%",
            padding: "2.25rem 2rem",
            background: "var(--color-bg-hemocyanin)",
            border: "1px solid rgba(255,62,104,0.4)",
            borderRadius: "var(--border-radius, 12px)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
            position: "relative",
            zIndex: 2,
          }}
        >
          <p
            style={{
              fontSize: "0.72rem",
              color: "var(--color-error-light, #FF809B)",
              fontWeight: 500,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: "0.5rem",
            }}
          >
            Sign-in not completing
          </p>
          <h1
            style={{
              fontSize: "1.55rem",
              color: "var(--color-bg-ice)",
              marginBottom: "0.75rem",
              fontWeight: 500,
              letterSpacing: "-0.015em",
              lineHeight: 1.25,
            }}
          >
            Your browser isn&apos;t accepting our sign-in cookie.
          </h1>
          <p
            data-testid="auth-failed-message"
            style={{
              color: "var(--color-bg-purple-haze)",
              fontSize: "0.92rem",
              fontWeight: 300,
              lineHeight: 1.55,
              marginBottom: "1.25rem",
            }}
          >
            This is almost always caused by browser settings that block
            third-party or strict-tracking cookies. Two things to try:
          </p>
          <ol
            style={{
              color: "var(--color-bg-purple-haze)",
              fontSize: "0.9rem",
              fontWeight: 300,
              lineHeight: 1.65,
              marginBottom: "1.5rem",
              paddingLeft: "1.15rem",
            }}
          >
            <li>
              Open the site in a regular (non-private/incognito) window.
            </li>
            <li>
              Allow cookies for <strong style={{ color: "var(--color-bg-ice)" }}>{window.location.host}</strong> in
              your browser settings, then click &quot;Try sign-in again&quot; below.
            </li>
          </ol>
          <button
            onClick={() => {
              try { sessionStorage.removeItem("procops_auth_loop"); } catch { /* ignore */ }
              initiateSignIn();
            }}
            data-testid="auth-failed-retry-btn"
            style={{
              width: "100%",
              padding: "0.85rem 1rem",
              borderRadius: "var(--border-radius, 12px)",
              border: "2px solid transparent",
              background: "var(--color-primary-500, #F050F8)",
              color: "var(--color-bg-siphon, #100030)",
              fontWeight: 500,
              fontSize: "0.95rem",
              cursor: "pointer",
              fontFamily: "var(--font-family, 'Outfit', sans-serif)",
              transition: "background 200ms cubic-bezier(0.4,0,0.2,1)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-primary-600, #FF2FFB)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--color-primary-500, #F050F8)";
            }}
          >
            Try sign-in again
          </button>
        </div>
      </div>
    );
  }

  // Restricted-account error state — only shown when the OAuth flow returns
  // with ?error=restricted. We render the full branded card here so the user
  // understands why they were denied and can switch accounts.
  return (
    <div
      data-testid="login-page"
      style={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
        overflow: "hidden",
        background: "var(--color-bg-siphon)",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "-30%",
          right: "-20%",
          width: "900px",
          height: "700px",
          background:
            "radial-gradient(ellipse, rgba(255,62,104,0.20) 0%, rgba(88,64,255,0.10) 45%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: "2rem",
          left: "2rem",
          display: "flex",
          alignItems: "center",
          gap: "0.55rem",
          zIndex: 2,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 30% 30%, #FA98FF, #F050F8 55%, #600E6B)",
            boxShadow:
              "0 0 14px rgba(240,80,248,0.55), inset 0 0 8px rgba(255,255,255,0.15)",
          }}
        />
        <span
          style={{
            color: "var(--color-bg-ice)",
            fontWeight: 500,
            fontSize: "1.05rem",
            letterSpacing: "-0.01em",
          }}
        >
          ProcOps
        </span>
      </div>

      <div
        style={{
          maxWidth: "440px",
          width: "100%",
          padding: "2.25rem 2rem",
          background: "var(--color-bg-hemocyanin)",
          border: "1px solid rgba(164,159,200,0.22)",
          borderRadius: "var(--border-radius, 12px)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
          position: "relative",
          zIndex: 2,
        }}
      >
        <p
          style={{
            fontSize: "0.72rem",
            color: "var(--color-error-light, #FF809B)",
            fontWeight: 500,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: "0.5rem",
          }}
        >
          Access denied
        </p>
        <h1
          style={{
            fontSize: "1.65rem",
            color: "var(--color-bg-ice)",
            marginBottom: "0.5rem",
            fontWeight: 500,
            letterSpacing: "-0.015em",
            lineHeight: 1.2,
          }}
        >
          That account isn&apos;t authorised.
        </h1>
        <p
          data-testid="restricted-domain-message"
          style={{
            color: "var(--color-bg-purple-haze)",
            fontSize: "0.95rem",
            fontWeight: 300,
            lineHeight: 1.55,
            marginBottom: "1.5rem",
          }}
        >
          ProcOps is restricted to authorised Octopus Energy accounts. Sign in
          with a different Google account to continue.
        </p>

        <button
          onClick={initiateSignIn}
          data-testid="login-google-btn"
          style={{
            width: "100%",
            padding: "0.85rem 1rem",
            borderRadius: "var(--border-radius, 12px)",
            border: "2px solid transparent",
            background: "var(--color-primary-500, #F050F8)",
            color: "var(--color-bg-siphon, #100030)",
            fontWeight: 500,
            fontSize: "0.95rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.6rem",
            fontFamily: "var(--font-family, 'Outfit', sans-serif)",
            transition:
              "background 200ms cubic-bezier(0.4,0,0.2,1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--color-primary-600, #FF2FFB)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--color-primary-500, #F050F8)";
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#100030" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#100030" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#100030" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#100030" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
          </svg>
          Try a different account
        </button>
      </div>
    </div>
  );
}
